// frontend/src/components/admin/reseller/ResellerDetailView.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { adminAPI } from "../../../services/api";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ArrowBack, Visibility, GetApp } from "@mui/icons-material";

const ResellerDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [reseller, setReseller] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchResellerData = async () => {
      try {
        setLoading(true);
        const resellerResponse = await adminAPI.getResellerDetails(id);
        setReseller(resellerResponse.data);

        // Fetch transactions for this reseller
        const transactionsResponse = await adminAPI.getUserTransactions(id);
        setTransactions(transactionsResponse.data);

        // Fetch eSIM orders for this reseller
        const ordersResponse = await adminAPI.getResellerOrders(id);
        setOrders(ordersResponse.data);
      } catch (err) {
        setError(err.response?.data?.message || t("errors.fetchDataFailed"));
        enqueueSnackbar(t("errors.fetchDataFailed"), { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchResellerData();
  }, [id, enqueueSnackbar, t]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      signDisplay: "always",
    }).format(amount);
  };

  const formatDataUsage = (used, total) => {
    if (!total) return "-";
    const totalGB = (parseInt(total) / (1024 * 1024 * 1024)).toFixed(2);
    if (!used) return `0GB / ${totalGB}GB`;
    const usedGB = (used / (1024 * 1024 * 1024)).toFixed(2);
    return `${usedGB}GB / ${totalGB}GB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t("common.noData");
    try {
      const date = new Date(dateStr);
      return date.toString() !== "Invalid Date"
        ? date.toLocaleString()
        : t("common.noData");
    } catch (error) {
      console.error("Date formatting error:", error);
      return t("common.noData");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/admin/resellers")}
        >
          {t("common.actions.back")}
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/admin/resellers")}
        sx={{ mb: 3 }}
      >
        {t("admin.resellers.backToList")}
      </Button>

      {reseller && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h5">{reseller.username}</Typography>
                <Typography variant="body1" color="textSecondary">
                  {reseller.email}
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {t("admin.resellers.status")}:
                  <Chip
                    label={t(`status.${reseller.status}`)}
                    color={reseller.status === "active" ? "success" : "error"}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6">
                  {t("credits.balance")}:{" "}
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(reseller.creditBalance)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t("admin.resellers.markup")}: {reseller.markupPercentage}%
                </Typography>
                <Typography variant="body2">
                  {t("admin.resellers.allowedPackages")}:{" "}
                  {reseller.allowedPackages?.length || 0}
                </Typography>
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate(`/admin/resellers/edit/${id}`)}
              >
                {t("common.actions.edit")}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(`/admin/credits/adjust/${id}`)}
              >
                {t("admin.resellers.adjustCredits")}
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label={t("admin.resellers.transactions")} />
              <Tab label={t("admin.resellers.esimOrders")} />
            </Tabs>
            <Divider />

            {activeTab === 0 && (
              <Box p={2}>
                <Typography variant="h6" gutterBottom>
                  {t("admin.resellers.recentTransactions")}
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("transactions.columns.id")}</TableCell>
                        <TableCell>{t("transactions.columns.date")}</TableCell>
                        <TableCell>{t("transactions.columns.type")}</TableCell>
                        <TableCell>
                          {t("transactions.columns.amount")}
                        </TableCell>
                        <TableCell>
                          {t("transactions.columns.status")}
                        </TableCell>
                        <TableCell>{t("common.actions.actions")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.length > 0 ? (
                        transactions.map((transaction) => (
                          <TableRow key={transaction.id}>
                            <TableCell>
                              {transaction.id.substring(0, 8)}
                            </TableCell>
                            <TableCell>
                              {formatDate(transaction.createdAt)}
                            </TableCell>
                            <TableCell>
                              {t(`transactions.types.${transaction.type}`, {
                                defaultValue: transaction.type
                                  .replace("_", " ")
                                  .toUpperCase(),
                              })}
                            </TableCell>
                            <TableCell>
                              {formatCurrency(transaction.amount)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={t(`status.${transaction.status}`, {
                                  defaultValue:
                                    transaction.status.toUpperCase(),
                                })}
                                color={
                                  transaction.status === "completed"
                                    ? "success"
                                    : transaction.status === "pending"
                                    ? "warning"
                                    : "error"
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title={t("common.actions.viewDetails")}>
                                <IconButton size="small">
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            {t("transactions.noTransactions")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {activeTab === 1 && (
              <Box p={2}>
                <Typography variant="h6" gutterBottom>
                  {t("admin.resellers.esimOrders")}
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("esim.orders.table.orderNo")}</TableCell>
                        <TableCell>{t("esim.orders.table.status")}</TableCell>
                        <TableCell>{t("esim.orders.table.package")}</TableCell>
                        <TableCell>{t("esim.orders.table.iccid")}</TableCell>
                        <TableCell>
                          {t("esim.orders.table.purchaseDate")}
                        </TableCell>
                        <TableCell>
                          {t("esim.orders.table.dataUsage")}
                        </TableCell>
                        <TableCell>{t("common.actions.actions")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.length > 0 ? (
                        orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>{order.orderNo}</TableCell>
                            <TableCell>
                              <Chip
                                label={t(
                                  `esim.orders.statuses.${order.status}`,
                                  {
                                    defaultValue: order.status.toUpperCase(),
                                  }
                                )}
                                color={
                                  order.status === "IN_USE"
                                    ? "success"
                                    : order.status === "GOT_RESOURCE"
                                    ? "info"
                                    : order.status === "SUSPENDED"
                                    ? "warning"
                                    : "error"
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {order.package?.name !== "Unknown Package"
                                ? order.package?.name
                                : order.packageType || t("common.noData")}
                            </TableCell>
                            <TableCell>{order.iccid || "-"}</TableCell>
                            <TableCell>
                              {formatDate(order.transaction?.date)}
                            </TableCell>
                            <TableCell>
                              {formatDataUsage(
                                order.orderUsage,
                                order.totalVolume
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Tooltip
                                  title={t("common.actions.viewDetails")}
                                >
                                  <IconButton size="small">
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                                {order.qrCodeUrl && (
                                  <Tooltip
                                    title={t("common.actions.downloadQR")}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        window.open(order.qrCodeUrl, "_blank")
                                      }
                                    >
                                      <GetApp />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            {t("esim.orders.noOrders")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ResellerDetailView;
